import React, { useEffect } from "react";
import {
  Dialog,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { lightTheme } from "../styles/theme";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { releaseFocusFromBackground } from "../utils/modalFocus";

interface VideoPlayerModalProps {
  open: boolean;
  onClose: () => void;
  videoPath: string;
  title?: string;
}

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({
  open,
  onClose,
  videoPath,
  title,
}) => {
  useBodyScrollLock(open);

  useEffect(() => {
    if (open) {
      releaseFocusFromBackground();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableRestoreFocus
      disableScrollLock
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          maxHeight: "90vh",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxHeight: "90vh",
          backgroundColor: lightTheme.colors.pureBlack,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <IconButton
          autoFocus
          onClick={onClose}
          aria-label="Close video"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            backgroundColor: lightTheme.colors.blackOverlay50,
            color: "common.white",
            "&:hover": {
              backgroundColor: lightTheme.colors.blackOverlay70,
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {title && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              zIndex: 10,
              backgroundColor: lightTheme.colors.blackOverlay70,
              color: "common.white",
              px: 2,
              py: 1,
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" sx={{ color: "common.white", fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
        )}

        <Box
          component="video"
          src={videoPath}
          controls
          autoPlay
          playsInline
          sx={{
            display: "block",
            maxWidth: "100%",
            maxHeight: "90vh",
            width: "auto",
            height: "auto",
            objectFit: "contain",
          }}
        />
      </Box>
    </Dialog>
  );
};

export default VideoPlayerModal;
